import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { Menu, X } from "lucide-react";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import { NewHeroSection } from "./components/NewHeroSection";
import PressMarquee from "./components/PressMarquee";
import MarketOpportunity from "./components/MarketOpportunity";
import WhyAgntHub from "./components/WhyAgntHub";
import InfluencerCarousel from "./components/InfluencerCarousel";
import HowItWorks from "./components/HowItWorks";
import AgentLibrary from "./components/AgentLibrary";
import { AgntProducts } from "./components/AgntProducts";
import NeobankCards from "./components/NeobankCards";
import TokenUtility from "./components/TokenUtility";
import NewTokenomics from "./components/NewTokenomics";
import NewRoadmap from "./components/NewRoadmap";
import FinalCTA from "./components/FinalCTA";
import LandingFAQSection from "./components/LandingFAQSection";
import { DarkFooter } from "./components/DarkFooter";

const NAV_LINKS = [
  { id: "token", label: "Token" },
  { id: "agents", label: "Agents" },
  { id: "neobank", label: "Neobank" },
];

export function LandingPageContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.12], [1, 0.96]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const smoothScroll = (id: string) => {
    const isMobile = mobileMenuOpen;
    if (isMobile) {
      setMobileMenuOpen(false);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const navbarHeight = 80;
          const top =
            el.getBoundingClientRect().top + window.scrollY - navbarHeight;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const navbarHeight = 80;
        const top =
          el.getBoundingClientRect().top + window.scrollY - navbarHeight;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  };

  return (
    <div className="bg-[#0a0a0a] text-white font-inter selection:bg-white selection:text-black overflow-x-hidden">
      {/* Scroll progress indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent z-[60] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? "bg-[#0a0a0a]/70 backdrop-blur-2xl border-b border-white/[0.06] py-3"
            : "bg-transparent py-5 border border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <motion.a
            className="flex items-center cursor-pointer"
            href="/"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <AttsIcon className="size-14" />

            <span className="font-space-grotesk font-bold text-xl tracking-tight">
              AGNT.Hub
            </span>
          </motion.a>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((nav) => (
              <button
                key={nav.id}
                onClick={() => smoothScroll(nav.id)}
                className="text-sm font-medium text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/[0.05] transition-all duration-300"
              >
                {nav.label}
              </button>
            ))}
            <a
              href="https://docs.agnthub.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/[0.05] transition-all duration-300"
            >
              Whitepaper
            </a>
            <button
              onClick={() => smoothScroll("faq")}
              className="text-sm font-medium text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/[0.05] transition-all duration-300"
            >
              FAQ
            </button>
          </div>

          <div className="hidden md:flex">
            <motion.a
              href="/dashboard"
              className="bg-white text-black px-6 py-2.5 rounded-full text-sm font-bold tracking-wide relative overflow-hidden inline-block"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              Get $ATTS Token
            </motion.a>
          </div>

          <button
            className="md:hidden text-white/80"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="absolute top-full left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-2xl border-b border-white/5 p-6 flex flex-col gap-3 md:hidden overflow-hidden"
            >
              {NAV_LINKS.map((nav) => (
                <button
                  key={nav.id}
                  onClick={() => smoothScroll(nav.id)}
                  className="text-left font-medium text-lg py-2.5 text-white/70 hover:text-white transition-colors"
                >
                  {nav.label}
                </button>
              ))}
              <a
                href="https://docs.agnthub.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left font-medium text-lg py-2.5 text-white/70"
              >
                Whitepaper
              </a>
              <button
                onClick={() => smoothScroll("faq")}
                className="text-left font-medium text-lg py-2.5 text-white/70 hover:text-white transition-colors"
              >
                FAQ
              </button>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/dashboard");
                }}
                className="bg-white text-black px-6 py-4 rounded-full text-base font-bold mt-4 w-full block text-center"
              >
                Get $ATTS Token
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Page sections */}
      <NewHeroSection heroOpacity={heroOpacity} heroScale={heroScale} />
      <PressMarquee />
      <MarketOpportunity />
      <WhyAgntHub />
      <InfluencerCarousel />
      <HowItWorks />
      <AgentLibrary />
      <AgntProducts />
      <NeobankCards />
      <TokenUtility />
      <NewTokenomics />
      <NewRoadmap />
      <FinalCTA />
      <LandingFAQSection />

      <DarkFooter />
    </div>
  );
}
